import gsap, { ScrollTrigger, SplitText } from "@lib/gsap";

export const scrollTriggerDefaults = getScrolltriggerDefaults();

function getScrolltriggerDefaults() {
  let windowWidth = window.innerWidth;

  let defaults;
  if (windowWidth > 991) {
    defaults = {
      start: "top 80%",
      end: "top 30%",
      scrub: true,
    };
  } else {
    defaults = {
      start: "top 80%",
      end: "bottom 80%",
      scrub: true,
    };
  }

  return defaults;
}

export function headingAnimation(
  element: HTMLElement,
  selector: string,
): { tl: gsap.core.Timeline; revert: () => void } {
  const headings = element.querySelectorAll(selector);

  const splitHeadings = new SplitText(headings, {
    type: "lines, words, chars",
    charsClass: "char",
    linesClass: "line",
    wordsClass: "word",
  });

  const tl = gsap.timeline();

  splitHeadings.lines.forEach((e: Element) => {
    tl.from(
      e.querySelectorAll(".char"),
      {
        delay: 0.1,
        clipPath: "inset(0 0 0 100%)",
        stagger: 0.05,
      },
      "<+=0.02",
    );
  });

  return { tl, revert: () => splitHeadings.revert() };
}

export function paragraphAnimation(
  element: HTMLElement,
  selector: string,
): { tl: gsap.core.Timeline; revert: () => void } {
  const paras = element.querySelectorAll(selector);

  const splitPara = new SplitText(paras, {
    type: "lines, words, chars",
    charsClass: "char",
    linesClass: "u-overflow-hidden",
    wordsClass: "word",
  });

  const tl = gsap.timeline();

  splitPara.lines.forEach((e: Element) => {
    tl.from(
      e.querySelectorAll(".char"),
      {
        yPercent: 100,
      },
      "<+=0.05",
    );
  });

  return { tl, revert: () => splitPara.revert() };
}

export function lineAnimation(
  element: HTMLElement,
  direction: "down" | "across",
): gsap.core.Timeline {
  const line = element.querySelector('[data-element="line"]');

  const tl = gsap.timeline();
  const config = direction === "down" ? { scaleY: 0 } : { scaleX: 0 };

  tl.from(line, config);

  return tl;
}

export function cornerBlockAnimation(element: HTMLElement): gsap.core.Timeline {
  const blocks = element.querySelectorAll('[data-element="corner-block"]');

  const tl = gsap.timeline();

  blocks.forEach((block) => {
    const variant =
      block.getAttribute("data-wf--corner-cover-small--variant") ||
      block.getAttribute("data-wf--corner-cover-large--variant");

    const fromRight = variant === "top-right" || variant === "bottom-right";
    const xPercent = fromRight ? 100 : -100;
    const transformOrigin = variant!.replace("-", " ");

    tl.from(
      block,
      { autoAlpha: 0, xPercent, scale: 0.5, transformOrigin },
      "<",
    );
  });

  return tl;
}

export function videoAnimation(element: HTMLElement): gsap.core.Timeline {
  const videos = element.querySelectorAll("video");

  const tl = gsap.timeline();

  tl.from(videos, { clipPath: "inset(50% 50% 50% 50%)" });

  return tl;
}

const HIGHLIGHT_SELECTOR = [
  ".u-heading-highlight b",
  ".u-heading-highlight strong",
  ".u-heading-highlight-purple b",
  ".u-heading-highlight-purple strong",
  ".u-heading-highlight-static em",
  ".u-heading-highlight-yellow b",
  ".u-heading-highlight-yellow strong",
].join(", ");

export function highlightAnimation(): void {
  const elements = document.querySelectorAll(HIGHLIGHT_SELECTOR);

  elements.forEach((el) => {
    if (el.closest('[data-module="scrolling-text-block"]')) return;

    gsap.set(el, { "--highlight-scale": 0 });
    gsap.to(el, {
      "--highlight-scale": 1,
      ease: "none",
      scrollTrigger: {
        ...scrollTriggerDefaults,
        end: "top 50%",
        trigger: el,
      },
    });
  });
}

export function navigationAnimation(): gsap.core.Timeline {
  const nav = document.querySelector("[data-nav]");

  const tl = gsap.timeline();

  tl.from(nav, { yPercent: -100 });

  return tl;
}

function normalizePath(path: string): string {
  const stripped = path.replace(/\/+$/, "").toLowerCase();
  return stripped === "" ? "/" : stripped;
}

export function setNavActiveState(): void {
  const nav = document.querySelector("[data-nav]");
  if (!nav) return;

  const buttons = nav.querySelectorAll<HTMLElement>("[data-button]");
  const current = normalizePath(window.location.pathname);

  buttons.forEach((button) => {
    const link = button.querySelector<HTMLAnchorElement>("a[href]");
    if (!link) return;

    let linkUrl: URL;
    try {
      linkUrl = new URL(link.href, window.location.origin);
    } catch {
      return;
    }

    // External links are never active
    const isExternal =
      linkUrl.origin !== window.location.origin || link.target === "_blank";

    const linkPath = normalizePath(linkUrl.pathname);
    const isActive =
      !isExternal &&
      (linkPath === current ||
        (linkPath !== "/" && current.startsWith(linkPath + "/")));

    button.setAttribute("data-trigger", isActive ? "on" : "hover focus");
  });
}
